import React, { useState, useEffect } from 'react';
import { customerApi, formatApiError } from '../services/api';
import { 
  Users, Plus, MagnifyingGlass, PencilSimple, 
  Trash, Envelope, Phone, MapPin, IdentificationCard, Buildings 
} from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import CustomerAvatar from '../components/CustomerAvatar';
import LicenseClassPicker, { LicenseClassBadges } from '../components/LicenseClassPicker';
import IdScanCard from '../components/IdScanCard';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    customer_type: 'private',
    company_name: '',
    company_legal_form: '',
    tax_number: '',
    vat_id: '',
    register_number: '',
    register_court: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'Deutschland',
    driver_license_number: '',
    driver_license_expiry: '',
    driver_license_classes: ['B'],
    date_of_birth: '',
    notes: ''
  });

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      const response = await customerApi.getAll({ search });
      setCustomers(response.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      loadCustomers();
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await customerApi.update(editingCustomer.id, formData);
        toast.success('Kunde aktualisiert');
      } else {
        await customerApi.create(formData);
        toast.success('Kunde hinzugefügt');
      }
      setShowModal(false);
      setEditingCustomer(null);
      resetForm();
      loadCustomers();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Kunde wirklich löschen?')) return;
    try {
      await customerApi.delete(id);
      toast.success('Kunde gelöscht');
      loadCustomers();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      customer_type: customer.customer_type || (customer.company_name ? 'business' : 'private'),
      company_name: customer.company_name || '',
      company_legal_form: customer.company_legal_form || '',
      tax_number: customer.tax_number || '',
      vat_id: customer.vat_id || '',
      register_number: customer.register_number || '',
      register_court: customer.register_court || '',
      first_name: customer.first_name,
      last_name: customer.last_name,
      email: customer.email,
      phone: customer.phone,
      address: customer.address,
      city: customer.city,
      postal_code: customer.postal_code,
      country: customer.country,
      driver_license_number: customer.driver_license_number,
      driver_license_expiry: customer.driver_license_expiry,
      driver_license_classes: customer.driver_license_classes || [],
      date_of_birth: customer.date_of_birth,
      notes: customer.notes || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      customer_type: 'private',
      company_name: '',
      company_legal_form: '',
      tax_number: '',
      vat_id: '',
      register_number: '',
      register_court: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      postal_code: '',
      country: 'Deutschland',
      driver_license_number: '',
      driver_license_expiry: '',
      driver_license_classes: ['B'],
      date_of_birth: '',
      notes: ''
    });
  };


  const handleIdScan = (data, photo, docType) => {
    if (!data) return;
    setFormData((prev) => ({
      ...prev,
      first_name: data.first_name || prev.first_name,
      last_name: data.last_name || prev.last_name,
      date_of_birth: data.date_of_birth || prev.date_of_birth,
      address: data.address || prev.address,
      postal_code: data.postal_code || prev.postal_code,
      city: data.city || prev.city,
      country: data.nationality
        ? (data.nationality.toLowerCase().includes('deutsch') ? 'Deutschland' : data.nationality)
        : prev.country,
      driver_license_number: data.driver_license_number || prev.driver_license_number,
      driver_license_expiry: (docType === 'fuehrerschein' && data.expiry_date)
        ? data.expiry_date
        : prev.driver_license_expiry,
      driver_license_classes: (data.license_classes && data.license_classes.length > 0)
        ? data.license_classes
        : prev.driver_license_classes,
    }));
  };

  return (
    <div className="p-6 space-y-6" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#111418]">Kunden</h1>
          <p className="text-[#525866] mt-1">{customers.length} Kunden registriert</p>
        </div>
        <Button
          onClick={() => { resetForm(); setEditingCustomer(null); setShowModal(true); }}
          className="btn-primary"
          data-testid="add-customer-btn"
        >
          <Plus weight="bold" className="mr-2" size={18} />
          Kunde hinzufügen
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A94A6]" size={20} />
        <Input
          type="text"
          placeholder="Suchen nach Name oder E-Mail..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 input-swiss"
          data-testid="customer-search-input"
        />
      </div>

      {/* Customer List */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="card-swiss h-24 skeleton" />
          ))}
        </div>
      ) : customers.length === 0 ? (
        <div className="card-swiss p-12 text-center">
          <Users weight="thin" size={64} className="mx-auto text-[#8A94A6] mb-4" />
          <p className="text-[#525866]">Keine Kunden gefunden</p>
        </div>
      ) : (
        <div className="card-swiss overflow-hidden">
          <table className="table-swiss">
            <thead>
              <tr>
                <th>Kunde</th>
                <th>Kontakt</th>
                <th>Adresse</th>
                <th>Führerschein</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {customers.map((customer) => (
                <tr key={customer.id} data-testid={`customer-row-${customer.id}`}>
                  <td>
                    <Link to={`/customers/${customer.id}`} className="hover:underline">
                      <div className="flex items-center gap-2">
                        <CustomerAvatar customer={customer} size={32} />
                        <div>
                          <p className="font-semibold text-[#111418] flex items-center gap-2">
                            {(customer.customer_type === 'business' || customer.company_name)
                              ? (customer.company_name || `${customer.first_name} ${customer.last_name}`)
                              : `${customer.first_name} ${customer.last_name}`}
                            {(customer.customer_type === 'business' || customer.company_name) && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide bg-[#EEF2FF] text-[#3730A3] px-1.5 py-0.5 rounded" data-testid={`customer-business-badge-${customer.id}`}>
                                <Buildings weight="bold" size={10} /> Firma
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-[#8A94A6]">
                            {(customer.customer_type === 'business' || customer.company_name)
                              ? `Ansprechpartner: ${customer.first_name} ${customer.last_name}`
                              : `Geb. ${new Date(customer.date_of_birth).toLocaleDateString('de-DE')}`}
                          </p>
                        </div>
                      </div>
                    </Link>
                  </td>
                  <td>
                    <div className="flex items-center gap-1 text-sm text-[#525866]">
                      <Envelope weight="bold" size={14} />
                      {customer.email}
                    </div>
                    <div className="flex items-center gap-1 text-sm text-[#525866]">
                      <Phone weight="bold" size={14} />
                      {customer.phone}
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-1 text-sm text-[#525866]">
                      <MapPin weight="bold" size={14} />
                      <span>{customer.postal_code} {customer.city}</span>
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-1 text-sm text-[#525866]">
                      <IdentificationCard weight="bold" size={14} />
                      {customer.driver_license_number}
                    </div>
                    <p className="text-xs text-[#8A94A6]">
                      Gültig bis {new Date(customer.driver_license_expiry).toLocaleDateString('de-DE')}
                    </p>
                    {customer.driver_license_classes?.length > 0 && (
                      <div className="mt-1">
                        <LicenseClassBadges classes={customer.driver_license_classes} />
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(customer)}
                        data-testid={`edit-customer-${customer.id}`}
                      >
                        <PencilSimple weight="bold" size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(customer.id)}
                        className="text-[#E60000] hover:bg-red-50"
                        data-testid={`delete-customer-${customer.id}`}
                      >
                        <Trash weight="bold" size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editingCustomer ? 'Kunde bearbeiten' : 'Neuer Kunde'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* KI-Ausweis-Scanner */}
            <IdScanCard onExtracted={handleIdScan} customerId={editingCustomer?.id} />
            {/* Kundenart Toggle */}
            <div>
              <Label className="overline">Kundenart</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, customer_type: 'private' })}
                  data-testid="customer-type-private-btn"
                  className={`px-3 py-2 rounded-lg border text-sm font-medium transition ${
                    formData.customer_type !== 'business'
                      ? 'bg-[#111418] text-white border-[#111418]'
                      : 'bg-white text-[#525866] border-[#E1E4E8] hover:bg-[#F5F7FA]'
                  }`}
                >
                  Privatkunde
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, customer_type: 'business' })}
                  data-testid="customer-type-business-btn"
                  className={`px-3 py-2 rounded-lg border text-sm font-medium transition inline-flex items-center justify-center gap-2 ${
                    formData.customer_type === 'business'
                      ? 'bg-[#111418] text-white border-[#111418]'
                      : 'bg-white text-[#525866] border-[#E1E4E8] hover:bg-[#F5F7FA]'
                  }`}
                >
                  <Buildings weight="bold" size={14} /> Firmenkunde
                </button>
              </div>
            </div>

            {/* Firmendaten (nur bei Firmenkunde) */}
            {formData.customer_type === 'business' && (
              <div className="border-l-2 border-[#3730A3] pl-4 space-y-4 bg-[#F8F9FB] rounded-r-lg py-3 pr-3" data-testid="business-fields-section">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#3730A3]">Firmendaten</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 sm:col-span-1">
                    <Label className="overline">Firmenname *</Label>
                    <Input
                      value={formData.company_name}
                      onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                      className="input-swiss"
                      required={formData.customer_type === 'business'}
                      data-testid="customer-company-name-input"
                      placeholder="Muster GmbH"
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <Label className="overline">Rechtsform</Label>
                    <Input
                      value={formData.company_legal_form}
                      onChange={(e) => setFormData({...formData, company_legal_form: e.target.value})}
                      className="input-swiss"
                      data-testid="customer-company-legal-form-input"
                      placeholder="GmbH, AG, UG …"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="overline">USt-IdNr.</Label>
                    <Input
                      value={formData.vat_id}
                      onChange={(e) => setFormData({...formData, vat_id: e.target.value})}
                      className="input-swiss"
                      data-testid="customer-vat-id-input"
                      placeholder="DE123456789"
                    />
                  </div>
                  <div>
                    <Label className="overline">Steuernummer</Label>
                    <Input
                      value={formData.tax_number}
                      onChange={(e) => setFormData({...formData, tax_number: e.target.value})}
                      className="input-swiss"
                      data-testid="customer-tax-number-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="overline">Handelsregister-Nr.</Label>
                    <Input
                      value={formData.register_number}
                      onChange={(e) => setFormData({...formData, register_number: e.target.value})}
                      className="input-swiss"
                      data-testid="customer-register-number-input"
                      placeholder="HRB 12345"
                    />
                  </div>
                  <div>
                    <Label className="overline">Registergericht</Label>
                    <Input
                      value={formData.register_court}
                      onChange={(e) => setFormData({...formData, register_court: e.target.value})}
                      className="input-swiss"
                      data-testid="customer-register-court-input"
                      placeholder="Amtsgericht München"
                    />
                  </div>
                </div>
              </div>
            )}

            {formData.customer_type === 'business' && (
              <p className="text-xs text-[#525866] -mb-2">
                <span className="font-semibold">Ansprechpartner</span> – die folgenden Angaben beziehen sich auf die vertretungsberechtigte Person / den Fahrer.
              </p>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="overline">Vorname</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  className="input-swiss"
                  required
                  data-testid="customer-firstname-input"
                />
              </div>
              <div>
                <Label className="overline">Nachname</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  className="input-swiss"
                  required
                  data-testid="customer-lastname-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="overline">E-Mail</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="input-swiss"
                  required
                  data-testid="customer-email-input"
                />
              </div>
              <div>
                <Label className="overline">Telefon</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="input-swiss"
                  required
                />
              </div>
            </div>

            <div>
              <Label className="overline">Geburtsdatum</Label>
              <Input
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                className="input-swiss"
                required
              />
            </div>

            <div>
              <Label className="overline">Adresse</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="input-swiss"
                required
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="overline">PLZ</Label>
                <Input
                  value={formData.postal_code}
                  onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                  className="input-swiss"
                  required
                />
              </div>
              <div>
                <Label className="overline">Stadt</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className="input-swiss"
                  required
                />
              </div>
              <div>
                <Label className="overline">Land</Label>
                <Input
                  value={formData.country}
                  onChange={(e) => setFormData({...formData, country: e.target.value})}
                  className="input-swiss"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="overline">Führerscheinnummer</Label>
                <Input
                  value={formData.driver_license_number}
                  onChange={(e) => setFormData({...formData, driver_license_number: e.target.value})}
                  className="input-swiss"
                  required
                />
              </div>
              <div>
                <Label className="overline">Führerschein gültig bis</Label>
                <Input
                  type="date"
                  value={formData.driver_license_expiry}
                  onChange={(e) => setFormData({...formData, driver_license_expiry: e.target.value})}
                  className="input-swiss"
                  required
                />
              </div>
            </div>

            <LicenseClassPicker
              value={formData.driver_license_classes}
              onChange={(arr) => setFormData({ ...formData, driver_license_classes: arr })}
            />

            <div>
              <Label className="overline">Notizen</Label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="input-swiss w-full px-3 py-2 min-h-[80px]"
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Abbrechen
              </Button>
              <Button type="submit" className="btn-primary" data-testid="save-customer-btn">
                {editingCustomer ? 'Speichern' : 'Hinzufügen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;
